import { Injectable, signal } from '@angular/core';
import { INVOICES_TABLE_FILTER_OPTIONS } from '@anduin/api';
import { buildActiveFilters } from '@anduin/shared';
import { injectInvoicesTracking } from './invoices-tracking.providers';
import {
  InvoicesBaseMetadata,
  InvoicesStepMetadataInput,
  InvoicesSurface,
  InvoicesWorkflowStep,
  InvoicesWorkflowStepMetaDataMap,
} from './invoices-workflow.models';

/**
 * Per-container tracking facade for Invoices. Wraps the shared `TrackWorkflowService`
 * and stamps `InvoicesBaseMetadata` (statusFilter, surface, activeFilters) onto every
 * event so call sites only pass step extras.
 *
 * The shared invoices table is mounted by two surfaces (`STANDALONE` Invoices page,
 * `CLIENT_DETAIL` invoices tab). Each surface owns its own setter-driven state, so the
 * service is registered per-container (`providers: [InvoicesTrackService]`) — root scope
 * would let one surface's `_columnFilters` leak into the other on navigation.
 */
@Injectable()
export class InvoicesTrackService {
  private readonly tracker = injectInvoicesTracking();

  private readonly _surface = signal<InvoicesSurface>('STANDALONE');
  private readonly _statusFilter = signal<INVOICES_TABLE_FILTER_OPTIONS>(INVOICES_TABLE_FILTER_OPTIONS.ALL);
  private readonly _searchValue = signal<string>('');
  private readonly _columnFilters = signal<Record<string, unknown>>({});

  setSurface(surface: InvoicesSurface): void {
    this._surface.set(surface);
  }

  setStatusFilter(filter: INVOICES_TABLE_FILTER_OPTIONS | null | undefined): void {
    this._statusFilter.set(filter ?? INVOICES_TABLE_FILTER_OPTIONS.ALL);
  }

  setSearchValue(value: string | null | undefined): void {
    this._searchValue.set(value ?? '');
  }

  setColumnFilters(model: Record<string, unknown> | null | undefined): void {
    this._columnFilters.set(model ?? {});
  }

  trackWorkflow<E extends InvoicesWorkflowStep>(step: E, metadata?: InvoicesStepMetadataInput<E>): void {
    const merged = { ...this.resolveBaseMetadata(), ...(metadata ?? {}) } as InvoicesWorkflowStepMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  private resolveBaseMetadata(): InvoicesBaseMetadata {
    return {
      statusFilter: this._statusFilter(),
      surface: this._surface(),
      activeFilters: this.resolveActiveFilters(),
    };
  }

  private resolveActiveFilters(): string {
    const tokens: string[] = [];
    if (this._searchValue().trim().length > 0) {
      tokens.push('search');
    }
    for (const [columnId, model] of Object.entries(this._columnFilters())) {
      if (model != null) {
        tokens.push(`column:${columnId}`);
      }
    }
    const statusFilter = this._statusFilter();
    if (statusFilter !== INVOICES_TABLE_FILTER_OPTIONS.ALL) {
      tokens.push(`statusFilter:${statusFilter}`);
    }
    return buildActiveFilters(tokens);
  }
}
